// /src/pages/private/members/personalhub/dashboard/cards/DashboardHero.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ColorPicker from '../../../../../../components/tools/ColorPicker';
import DonorGemBadge from '../../../../../../components/donate/DonorGemBadge';
import KoFiModal from '../../../../../../components/KoFiModal';
import { useSecurity } from '../../../../../../SecurityContext';
import discordAvatarDefault from '../../../../../../medias/avatar_default.png';
import eToken from '../../../../../../medias/event-token.png';
import mToken from '../../../../../../medias/mission-token.png';
import { normalizeDonorGem } from '../../../../../../utils/donorGemDisplay';
import HeroTokenSegment from '../HeroTokenSegment';
import { getParticipationTokenState } from '../participationTokens';
import { displayHandle } from '../dashboardUtils';
import { saveDashboardHeroRingColor } from '../dashboardHeroRingColorApi';
import {
  DASHBOARD_HERO_RING_DEFAULT_COLOR,
  getDashboardHeroRingBorderClass,
  getDashboardHeroRingHaloClass,
  resolveDashboardHeroRingHex,
  sanitizeDashboardHeroRingHex,
} from '../dashboardHeroRingColor';

const HERO_RING_COLOR_RESOURCE = 'private.navbar.personalhub.dashboard.heroRingColor';
const HERO_RING_HALO_RESOURCE = 'private.navbar.personalhub.dashboard.heroRingHalo';
const COLOR_PICKER_ID = 'dashboard-hero-ring';

export default function DashboardHero({
  player,
  loading,
  moniker,
  handleId,
  onDiscordAvatarError,
  onRingColorSaved,
  allowColorPickerEdit = true,
}) {
  const { subjectHasPermission } = useSecurity();
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [savingColor, setSavingColor] = useState(false);
  const [showKoFi, setShowKoFi] = useState(false);

  const onDonorGemClick = useCallback((e) => {
    e?.stopPropagation?.();
    setShowKoFi(true);
  }, []);

  const canUseCustomColor = subjectHasPermission(HERO_RING_COLOR_RESOURCE, 'write');

  const canEditColor = allowColorPickerEdit && Boolean(handleId) && canUseCustomColor;

  const canShowHalo = subjectHasPermission(HERO_RING_HALO_RESOURCE, 'read');

  const storedColor = player?.dashboardHeroRingColor ?? null;
  const effectiveColor = useMemo(
    () => resolveDashboardHeroRingHex(storedColor, canUseCustomColor),
    [storedColor, canUseCustomColor],
  );

  const [ringColor, setRingColor] = useState(effectiveColor);

  useEffect(() => {
    setRingColor(effectiveColor);
  }, [effectiveColor]);

  const ringBorderClass = useMemo(
    () => getDashboardHeroRingBorderClass(ringColor),
    [ringColor],
  );

  const haloClass = useMemo(
    () => (canShowHalo ? getDashboardHeroRingHaloClass(ringColor) : ''),
    [canShowHalo, ringColor],
  );

  const donorGem = useMemo(() => normalizeDonorGem(player), [player]);

  const handleColorPickerToggle = useCallback((pickerId) => {
    setActiveColorPicker((prev) => (prev === pickerId ? null : pickerId));
  }, []);

  const handleRingColorChange = useCallback(async (nextColor) => {
    const sanitized = sanitizeDashboardHeroRingHex(nextColor)
      || DASHBOARD_HERO_RING_DEFAULT_COLOR;
    setRingColor(sanitized);

    if (!canEditColor || !handleId || savingColor) return;

    setSavingColor(true);
    try {
      const result = await saveDashboardHeroRingColor(handleId, sanitized);
      if (result?.ok) {
        onRingColorSaved?.(result.dashboardHeroRingColor || sanitized);
      }
    } finally {
      setSavingColor(false);
    }
  }, [canEditColor, handleId, onRingColorSaved, savingColor]);

  const avatarAlt = moniker !== '—' ? moniker : 'Member';
  const { redacted, mission, events, total } = getParticipationTokenState(player);

  return (
    <section className="member-dashboard__hero" aria-label="Member summary">
      {donorGem?.showBadge ? (
        <DonorGemBadge
          className="member-dashboard__donor-chip"
          donorGem={donorGem}
          showLabel
          onClick={onDonorGemClick}
        />
      ) : null}

      <KoFiModal isOpen={showKoFi} onClose={() => setShowKoFi(false)} />

      <div className="member-dashboard__hero-inner">
        <div className="member-dashboard__identity">
          <div className="member-dashboard__avatar-stack">
            {canShowHalo ? (
              <div
                className={[
                  'member-dashboard__avatar-halo',
                  haloClass,
                ].filter(Boolean).join(' ')}
                data-resource={HERO_RING_HALO_RESOURCE}
                data-action="read"
                aria-hidden
              />
            ) : null}

            <img
              className="member-dashboard__avatar-discord"
              src={player?.discordAvatar || discordAvatarDefault}
              onError={onDiscordAvatarError}
              alt={avatarAlt}
            />

            <div
              className={[
                'member-dashboard__avatar-ring',
                ringBorderClass,
              ].filter(Boolean).join(' ')}
              aria-hidden
            />

            {canEditColor ? (
              <div
                className="member-dashboard__avatar-color-picker"
                data-resource={HERO_RING_COLOR_RESOURCE}
                data-action="write"
                data-route="/api/user/private-dashboard/hero-ring-color-save"
              >
                <div className="member-dashboard__avatar-color-picker__trigger">
                  <ColorPicker
                    color={ringColor}
                    setColor={handleRingColorChange}
                    activeColorPicker={activeColorPicker}
                    setActiveColorPicker={setActiveColorPicker}
                    onToggle={handleColorPickerToggle}
                    colorPickerId={COLOR_PICKER_ID}
                    disabled={savingColor}
                  />
                  <span
                    className="member-dashboard__avatar-color-picker__tooltip"
                    role="tooltip"
                  >
                    donor perk
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <h1 className="member-dashboard__moniker">
            {loading && !player ? '…' : moniker}
          </h1>

          <p className="member-dashboard__subline">
            <span className="member-dashboard__subline-label">RSI handle</span>
            <span className="member-dashboard__subline-value">
              {displayHandle(player)}
            </span>
          </p>
        </div>

        <div className="member-dashboard__divider" aria-hidden />

        <div className="member-dashboard__meta">
          <div className="member-dashboard__rank-row">
            <div className="member-dashboard__rank-icon-slot">
              {player?.rankImageUrl ? (
                <img
                  className="member-dashboard__rank-icon"
                  src={player.rankImageUrl}
                  alt=""
                />
              ) : (
                <span
                  className="member-dashboard__rank-icon--empty"
                  aria-hidden
                />
              )}
            </div>

            <div className="member-dashboard__rank-text">
              <p className="member-dashboard__rank-name">
                {player?.rankName || '—'}
              </p>

              <p className="member-dashboard__pay-grade">
                {player?.payGrade ? `Pay grade ${player.payGrade}` : '—'}
              </p>

              {player?.rosterAssignmentDisplay ? (
                <p className="member-dashboard__assignment">
                  {player.rosterAssignmentDisplay}
                </p>
              ) : null}
            </div>
          </div>

          <div className="member-dashboard__org-sep" aria-hidden />

          <div className="member-dashboard__org-row">
            <HeroTokenSegment
              label="Event token"
              iconSrc={eToken}
              loading={loading}
              player={player}
              redacted={redacted}
              value={events}
            />

            <div className="member-dashboard__org-vrule" aria-hidden />

            <HeroTokenSegment
              label="Mission token"
              iconSrc={mToken}
              loading={loading}
              player={player}
              redacted={redacted}
              value={mission}
            />

            <div className="member-dashboard__org-vrule" aria-hidden />

            <HeroTokenSegment
              label="Service XP"
              hideIcon
              loading={loading}
              player={player}
              redacted={redacted}
              value={total}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
